/**
 * Sanitize and prepare HTML content from rich text editor
 * Ensures only safe HTML tags are allowed
 */
export function sanitizeRichText(html: string): string {
  if (!html) return '';

  // Allow only specific tags and remove all others
  const allowedTags = ['b', 'strong', 'i', 'em', 'u', 'h3', 'ul', 'ol', 'li', 'br', 'p'];

  // Create a temporary div to parse the HTML
  const temp = document.createElement('div');
  temp.innerHTML = html;

  // Recursively clean the HTML
  function cleanNode(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || '';
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      const tagName = element.tagName.toLowerCase();

      if (allowedTags.includes(tagName)) {
        const children = Array.from(element.childNodes)
          .map(child => cleanNode(child))
          .join('');
        return `<${tagName}>${children}</${tagName}>`;
      } else {
        // If tag is not allowed, just return the children
        return Array.from(element.childNodes)
          .map(child => cleanNode(child))
          .join('');
      }
    }

    return '';
  }

  return Array.from(temp.childNodes)
    .map(node => cleanNode(node))
    .join('');
}

/**
 * Server-side HTML sanitization (without DOM)
 */
export function sanitizeRichTextServer(html: string): string {
  if (!html) return '';

  // Basic HTML entity encoding for security
  const escapeHtml = (str: string): string => {
    const htmlEscapes: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };

    return str.replace(/[&<>"']/g, (match) => htmlEscapes[match]);
  };

  // Allow only specific HTML tags
  const allowedTags = ['b', 'strong', 'i', 'em', 'u', 'h3', 'ul', 'ol', 'li', 'br', 'p'];

  // First, escape all HTML
  let safe = escapeHtml(html);

  // Then selectively unescape allowed tags
  allowedTags.forEach(tag => {
    // Opening tags
    safe = safe.replace(new RegExp(`&lt;${tag}&gt;`, 'gi'), `<${tag}>`);
    // Closing tags
    safe = safe.replace(new RegExp(`&lt;/${tag}&gt;`, 'gi'), `</${tag}>`);
    // Self-closing br
    if (tag === 'br') {
      safe = safe.replace(/&lt;br\s*\/?&gt;/gi, '<br>');
    }
  });

  return safe;
}

/**
 * Convert rich text HTML to plain text
 * Useful for previews, meta descriptions, etc.
 */
export function richTextToPlainText(html: string): string {
  if (!html) return '';

  return html
    // Remove HTML tags
    .replace(/<[^>]*>/g, '')
    // Decode HTML entities
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    // Clean up whitespace
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Render rich text content safely for display
 * Returns an object suitable for dangerouslySetInnerHTML
 */
export function renderRichText(html: string): { __html: string } {
  // Check if we're on the client side
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    return { __html: sanitizeRichText(html) };
  }
  // Use server-side sanitization as fallback
  return { __html: sanitizeRichTextServer(html) };
}

/**
 * Simple HTML sanitization that preserves formatting
 * This version works both on client and server
 */
export function sanitizeHtml(html: string): string {
  if (!html) return '';

  // Remove script tags and their content
  let cleaned = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

  // Remove on* event handlers
  cleaned = cleaned.replace(/\s*on\w+\s*=\s*"[^"]*"/gi, '');
  cleaned = cleaned.replace(/\s*on\w+\s*=\s*'[^']*'/gi, '');
  cleaned = cleaned.replace(/\s*on\w+\s*=\s*\{[^}]*\}/gi, '');

  // Remove javascript: protocol
  cleaned = cleaned.replace(/javascript:/gi, '');

  // Remove style attributes that could be malicious
  cleaned = cleaned.replace(/style\s*=\s*"[^"]*expression[^"]*"/gi, '');
  cleaned = cleaned.replace(/style\s*=\s*'[^']*expression[^']*'/gi, '');

  // Convert <br> at the end of paragraphs to create visible line breaks
  // Replace <p>content<br></p> with <p>content</p><br>
  cleaned = cleaned.replace(/<p>(.*?)<br\s*\/?>\s*<\/p>/gi, '<p>$1</p><br>');

  // The HTML from the rich text editor is already safe, just return it cleaned
  return cleaned;
}